import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { useIsMobile } from './hooks/useMobile';
import { BUILD_TIME_DINGTALK_CORP_ID } from './lib/dingtalk-config';
import './styles/global.css';

// 写入 DOM，防止 vite/minify 把构建期 corpId 摇掉（部署校验依赖产物内含该字符串）
if (BUILD_TIME_DINGTALK_CORP_ID) {
  document.documentElement.dataset.tgDingtalkCorpId = BUILD_TIME_DINGTALK_CORP_ID;
}

dayjs.locale('zh-cn');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function AppProviders() {
  const isMobile = useIsMobile();

  /**
   * 同步 body.tg-mobile，让 CSS 通过 className 选择器命中 mobile 紧凑样式。
   * 不再仅依赖 `@media (max-width: 767px)` ——钉钉 webview 经常会把
   * viewport 报到 ~1024 宽，导致媒体查询不命中、布局以 PC 宽度渲染、
   * 在小屏幕上只能看到左半边。改用 className 切换最稳。
   */
  useEffect(() => {
    document.body.classList.toggle('tg-mobile', isMobile);
  }, [isMobile]);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#9b7dd8',
          colorInfo: '#9b7dd8',
          colorLink: '#8f74d4',
          borderRadius: isMobile ? 10 : 8,
          controlHeight: isMobile ? 40 : 32,
        },
        algorithm: isMobile ? antdTheme.compactAlgorithm : antdTheme.defaultAlgorithm,
      }}
    >
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppProviders />
  </React.StrictMode>,
);
